import { Component, ElementRef, ViewChild, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { PanelModule } from 'primeng/panel';

import { AlertService } from '../../_service/alert.service';
import { DialogType, GlobalDialogService } from '../../_service/global-dialog.service';
import { AiSessionService, AiAskScope } from '../../_service/ai-session.service';
import { DataService, Note } from '../../_service/data.service';

@Component({
  selector: 'app-ai',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, TextareaModule, RouterModule, PanelModule],
  templateUrl: './ai.component.html',
  styleUrls: ['./ai.component.scss'],
})
export class AiComponent implements OnInit {
  @ViewChild('questionInput') questionInput?: ElementRef<HTMLTextAreaElement>;

  private readonly aiSessionService = inject(AiSessionService);
  private readonly alertService = inject(AlertService);
  private readonly globalDialogService = inject(GlobalDialogService);
  private readonly dataService = inject(DataService);
  private readonly cdr = inject(ChangeDetectorRef);

  // Subscribe to session state
  state$ = this.aiSessionService.state$;

  referencedNotesCollapsed = false;
  sourcesCollapsed = false;

  // Cache for full note details
  private noteDetailsCache: Map<string, Note | null> = new Map();

  ngOnInit(): void {
    // Subscribe to state changes and load note details when they become available
    this.state$.subscribe((state) => {
      if (state.usedNoteIds && state.usedNoteIds.length > 0) {
        state.usedNoteIds.forEach((noteId) => {
          if (!this.noteDetailsCache.has(noteId)) {
            this.loadNoteDetails(noteId);
          }
        });
      }
    });
  }

  async askQuestion(question: string): Promise<void> {
    if (!question.trim()) {
      this.showErrorDialog('Question required', 'Please enter a question before submitting.');
      return;
    }
    await this.aiSessionService.askQuestion(question);
  }

  focusQuestionInput(): void {
    // Defer to let the dialog finish its show animation before focusing
    setTimeout(() => this.questionInput?.nativeElement.focus(), 0);
  }

  onQuestionEnter(event: Event, question: string): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.shiftKey || keyboardEvent.ctrlKey || keyboardEvent.altKey || keyboardEvent.metaKey) {
      return;
    }

    keyboardEvent.preventDefault();
    void this.askQuestion(question);
  }

  onQueryChange(query: string): void {
    this.aiSessionService.setQuery(query ?? '');
  }

  clearDialog(): void {
    this.aiSessionService.clearResults();
    this.noteDetailsCache.clear();
    this.referencedNotesCollapsed = false;
    this.sourcesCollapsed = false;
  }

  toggleSourcesCollapsed(): void {
    this.sourcesCollapsed = !this.sourcesCollapsed;
  }

  setIncludeDebug(value: boolean): void {
    this.aiSessionService.setIncludeDebug(value);
  }

  readonly askModeOptions: Array<{ value: AiAskScope; label: string }> = [
    { value: 'all_notes', label: 'Ask all notes' },
    { value: 'filtered_notes', label: 'Ask filtered notes' },
    { value: 'single_note', label: 'Ask current note' },
  ];

  setAskScope(scope: AiAskScope): void {
    this.aiSessionService.setAskScope(scope);
  }

  canAskCurrentNote(session: { currentNoteContext?: { descriptionHtml: string; commentsHtml: string } }): boolean {
    const ctx = session?.currentNoteContext;
    return !!ctx && (!!ctx.descriptionHtml?.trim() || !!ctx.commentsHtml?.trim());
  }

  canAskFilteredNotes(session: { filteredNoteIds: string[] }): boolean {
    return Array.isArray(session?.filteredNoteIds) && session.filteredNoteIds.length > 0;
  }

  isModeAvailable(session: any, mode: AiAskScope): boolean {
    if (mode === 'single_note') {
      return this.canAskCurrentNote(session);
    }
    if (mode === 'filtered_notes') {
      return this.canAskFilteredNotes(session);
    }
    return true;
  }

  shouldShowReferencedNotes(session: { askScope: AiAskScope; answerSource?: string; answer?: string; usedNoteIds: string[]; sources: any[]; isLoading: boolean }): boolean {
    if (session.sources && session.sources.length > 0) {
      return false;
    }
    if (session.askScope === 'single_note' || session.answerSource === 'current_note' || session.answerSource === 'single_note') {
      return false;
    }
    return !!session.answer || session.isLoading || session.usedNoteIds.length > 0;
  }

  toggleReferencedNotes(): void {
    this.referencedNotesCollapsed = !this.referencedNotesCollapsed;
  }

  getNoteDetails(noteId: string): Note | undefined {
    return this.noteDetailsCache.get(noteId) ?? undefined;
  }

  formatReferenceDate(date?: string): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  getUniqueNoteIds(noteIds: string[]): string[] {
    return [...new Set(noteIds)];
  }

  getNoteLink(noteId: string): string[] {
    // Let Angular handle encoding; pre-encoding would double-encode ":" into "%253A"
    return ['/view', noteId];
  }

  private async loadNoteDetails(noteId: string): Promise<void> {
    if (this.noteDetailsCache.has(noteId)) {
      return; // Already loaded or loading
    }

    // Set a placeholder to prevent duplicate requests
    this.noteDetailsCache.set(noteId, null);

    try {
      this.dataService.getNote(noteId).subscribe({
        next: (response) => {
          if (response.status === 'success' && response.data) {
            this.noteDetailsCache.set(noteId, response.data);
          } else {
            this.noteDetailsCache.set(noteId, null);
          }
          // Trigger change detection so the UI updates
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Failed to load note details:', err);
          this.noteDetailsCache.set(noteId, null);
          this.cdr.markForCheck();
        },
      });
    } catch (err) {
      console.error('Failed to load note details:', err);
      this.noteDetailsCache.set(noteId, null);
    }
  }

  private showErrorDialog(title: string, detail: string) {
    this.globalDialogService.showGlobalDialog(title, '', detail, DialogType.Error);
  }
}
